import axios from "axios";

import { baseUrl } from "./constants";
import { getToken, removeAuth } from "./auth";

/**
 * The one HTTP client for the app.
 *
 * Every request carries the current token and a timeout, and an expired token
 * lands the member back on the login screen rather than leaving the UI stuck
 * on a spinner. Tokens last 24 hours, so this happens routinely.
 */
const api = axios.create({
  baseURL: baseUrl,
  // The backend can be slow to wake after a period of inactivity, but a
  // request that has not answered by now is not going to.
  timeout: 45000,
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Token ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      removeAuth();
      // Full reload rather than a router push: this can fire from anywhere,
      // including outside the router, and it clears all in-memory state.
      if (window.location.pathname !== "/login") {
        window.location.assign("/login");
      }
    }
    return Promise.reject(error);
  },
);

/** Pull a human-readable message out of a DRF error response. */
export function errorMessage(error, fallback = "Something went wrong.") {
  const data = error && error.response && error.response.data;
  if (!data) {
    if (error && error.code === "ECONNABORTED") {
      return "The server took too long to respond. Please try again.";
    }
    return fallback;
  }
  if (typeof data === "string") return data;
  if (data.message) return data.message;

  // DRF field errors: { field: ["message", ...] }
  const firstKey = Object.keys(data)[0];
  const value = firstKey && data[firstKey];
  if (Array.isArray(value)) return value[0];
  if (typeof value === "string") return value;
  return fallback;
}

export default api;
